import { useCallback, useEffect, useRef } from 'react';
import { useRequest, useUnmount } from 'ahooks';
import { AxiosRequestConfig, Method } from 'axios';
import http from '@/utils/http';

// 从Method类型中提取特定的大写方法
export type TFetchType = Extract<Method, 'POST' | 'PUT' | 'DELETE' | 'GET'>;

// 路径必须加api
export type AddSlashIfString<T> = T extends string ? `/api/${T}` : T;

// useRequest默认配置类型
export interface IQueryConfig<N = number> {
  // 失败重试次数
  retryCount: N;
  // 节流时长
  debounceWait?: N;
  // 小于300ms,不展示loading效果
  loadingDelay?: N;
  // 默认初始化执行
  manual: boolean;
  // 轮询页面消失请求挂起
  pollingWhenHidden: boolean;
  // 轮询请求失败次数
  pollingErrorRetryCount: N;
}

// 接口返回
export type RequestBack<T = any> = Promise<{
  // 数据列表,可自定义类型传入
  data: T;
  code: number;
  msg: string;
  req: { [key in string]: unknown };
}>;

// 定义配置项
export const queryInitStatClientConfig: IQueryConfig = {
  retryCount: 0,
  manual: false,
  pollingWhenHidden: false,
  pollingErrorRetryCount: 1,
};

/**
 *
 * 统一请求控制器钩子
 * 功能说明：
 * 1. 封装了HTTP请求的通用逻辑，包括请求发送、取消、超时处理等
 * 2. 自动处理组件卸载时的请求取消、优化请求竞速、重复请求问题、
 * 3. 提供请求成功/取消的回调机制
 * 4. 与ahooks的useRequest集成，提供缓存、重试等功能
 * 
 * 使用场景：
 * - 异步代码抽离
 * 
 * @example
 * const data = useRequestController({
    method: 'GET',
    url: `/api/xxx`,
  });
 */

export const useRequestController = <
  S extends string,
  F extends (...args: any[]) => void,
  T extends {
    // 接口名
    url: AddSlashIfString<S>;
    // 方法
    method: TFetchType;
    // 是否默认发起请求
    manual?: boolean;
    // 外部副作用依赖值, 变化会重新触发请求
    refreshDeps?: unknown;
    // 超时时间
    timeout?: number;
    // 请求成功的回调
    onSuccess?: F;
    httpCfg?: AxiosRequestConfig;
    // 取消回调
    onCancel?: F;
  },
>({
  url,
  method = 'POST',
  timeout = 120000,
  refreshDeps,
  manual,
  onSuccess,
  onCancel,
  httpCfg,
  ...restProps
}: T) => {
  // 设置方法key
  const setMethodType = method === 'GET' ? 'params' : 'data';
  // 取消请求
  const abortControllerRef = useRef<AbortController>();
  // 触发请求
  const fetch = async (params: any) => {
    // 如果存在之前的请求，取消它
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // 创建新的 AbortController
    abortControllerRef.current = new AbortController();
    try {
      const response = (await http(url, {
        method,
        [setMethodType]: params || refreshDeps,
        signal: abortControllerRef.current.signal, // 添加 signal 以支持取消
        timeout, // 设置axios超时时间
        ...httpCfg,
      })) as RequestBack;
      return response;
    } catch (error) {
      // 这里的异常情况兜底回调统一都在useRequest中的OnError处理, 这里在细化一下主动取消的请求,增加回调
      if ((error as { msg?: string; }).msg === 'canceled') {
        // 是主动取消的请求
        console.log('Request was cancelled');
        onCancel?.(error);
      } else if ((error as { msg?: string; }).msg?.includes('timeout')) {
        // 超时错误处理
        console.log('Request timeout');
        throw new Error('请求超时，请检查网络连接或稍后重试');
      }
      // 其他错误处理
      return Promise.reject(error);
    }
  };
  // 安全的触发请求
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const safeDispatch = useSafeDispatch(fetch, restProps?.leaveRequest);

  // 清理函数
  useUnmount(() => {
    // 组件卸载时取消未完成的请求（leaveRequest 为 true 时不取消，确保离开页面的保存请求能完成）
    if (!restProps?.leaveRequest) {
      cancelRequest();
    }
  });

  // 取消请求
  const cancelRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  // useRequest集成功能
  const data: any = useRequest(safeDispatch as any, {
    ...queryInitStatClientConfig,
    // cacheKey: url + method,
    manual,
    refreshDeps: [refreshDeps],
    onSuccess: (value) => {
      onSuccess?.(value, data?.mutate);
    },
    ...restProps,
  });

  return {
    ...data,
    cancelRequest,
  };
};

/**
 * 返回组件的挂载阶段，如果还在挂载或者已经卸载，返回false，反之，返回true,进行路由切换的时候，我们的组件是会处于销毁状态
 */
export const useMountedRef = () => {
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  });
  return mountedRef;
};

/**
 * 用来安全地触发 dispatch 的钩子
 * @param dispatch 用来识别当前组件是否已卸载，并且决定是否render()
 * @returns
 */
const useSafeDispatch = <T,>(dispatch: (...args: T[]) => void, leaveRequest?: boolean) => {
  const mountRef = useMountedRef();
  return useCallback(
    (...args: T[]) => (mountRef.current || leaveRequest ? dispatch(...args) : void 0),
    [dispatch, mountRef, leaveRequest],
  );
};
